import { UserProvider, useUserContext } from "@repo/baseCloud";
import { TLPComponents, TLPProviders } from "@repo/ui";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { BrowserRouter, Redirect, Route, Switch } from "react-router-dom";

import { Header } from "./components/header/header";
import { AppSettingsPage } from "./pages/appSettings/page";
import { BackgroundChecksPage } from "./pages/backgroundChecks/page";
import { CustomerIncidentsPage } from "./pages/customer-incidents/page";
import { DisciplinesPage } from "./pages/disciplines/page";
import { DrugTestsPage } from "./pages/drugTests/page";
import { EnrollmentValidationPage } from "./pages/enrollmentValidation/page";
import { FieldAuditsPage } from "./pages/fieldAudits/page";
import { Leads } from "./pages/leads/page";
import { LocationsPage } from "./pages/locations/page";
import { Auth } from "./pages/login/auth";
import { Maps } from "./pages/maps";
import { LayerMaps } from "./pages/mapsWithLayers";
import MyView from "./pages/myView";
import { PreEnrollmentValidationPage } from "./pages/preEnrollmentValidation/page";
import { RepsPage } from "./pages/representatives/page";

const rootElement = document.getElementById("root");

function App(props: Props) {
	const queryClient = new QueryClient({
		defaultOptions: {
			queries: {
				refetchOnWindowFocus: false,
				retry: false,
				staleTime: Infinity,
			},
		},
	});

	return (
		<>
			<QueryClientProvider client={queryClient}>
				<TLPProviders>
					<UserProvider>
						<BrowserRouter basename="/MVC">
							<Page {...props} />
							<TLPComponents />
						</BrowserRouter>
					</UserProvider>
				</TLPProviders>
				<ReactQueryDevtools initialIsOpen={false} />
			</QueryClientProvider>
		</>
	);
}

function Page(props: Props) {
	const { page } = props;
	const entityType = rootElement.getAttribute("data-entityType");
	const entityId = rootElement.getAttribute("data-entityId");
	switch (page) {
		case "login":
			return <Auth />;
		case "leads":
			return <Leads />;
		case "maps":
			return <Maps entityType={entityType} entityId={entityId} />;
		case "layerMaps":
			return <LayerMaps />;
		case "rep":
		case "reps":
			return <RepsPage />;
		case "customer-incidents":
			return <CustomerIncidentsPage />;
		case "disciplines":
			return <DisciplinesPage />;
		case "enrollment-validation":
			return <EnrollmentValidationPage />;
		case "pre-enrollment-validation":
			return <PreEnrollmentValidationPage />;
		case "locations":
			return <LocationsPage />;
		case "drug-tests":
			return <DrugTestsPage />;
		case "background-checks":
			return <BackgroundChecksPage />;
		case "field-audits":
			return <FieldAuditsPage />;
		case "my-view":
			return <MyView />;
		case "app-settings":
			return <AppSettingsPage />;
		default:
			return <DefaultPage />;
	}
}

function DefaultPage() {
	const { loggedIn, loading } = useUserContext();

	if (loading) return null;
	if (!loggedIn) return <Auth />;

	return (
		<div className="flex min-h-screen flex-col">
			<Header />
			<div className="flex-grow p-4">
				<Switch>
					{/* FOR TESTING/DEVELOPMENT */}
					<Route exact path="/" component={MyView} />

					<Route exact path="/reps" component={RepsPage} />
					<Route exact path="/locations" component={LocationsPage} />
					<Route exact path="/customer-incidents" component={CustomerIncidentsPage} />
					<Route exact path="/disciplines" component={DisciplinesPage} />
					<Route exact path="/leads" component={Leads} />
					<Route exact path="/maps" component={Maps} />
					<Route exact path="/layerMaps" component={LayerMaps} />
					<Route exact path="/enrollment-validation" component={EnrollmentValidationPage} />
					<Route exact path="/pre-enrollment-validation" component={PreEnrollmentValidationPage} />
					<Route exact path="/drug-tests" component={DrugTestsPage} />
					<Route exact path="/background-checks" component={BackgroundChecksPage} />
					<Route exact path="/field-audits" component={FieldAuditsPage} />
					<Route exact path="/my-view" component={MyView} />
					<Route exact path="/app-settings" component={AppSettingsPage} />

					<Redirect from="*" to="/" />
				</Switch>
			</div>
		</div>
	);
}

export default App;

interface Props {
	page?: string | null;
}
